// ===== 一覧 =====

import { ReactNode, useEffect, useState } from "react";
import {
  Stack, Typography, Paper, Alert, Button,
  Table, TableHead, TableBody, TableRow, TableCell, Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { DataLink } from "../../../components/links/EntityLinks";
import { issuanceRow } from "../../../schema/console/issuance";
import { ContentLoading } from "../../../components/Loading";
import { formatUserWithGroup } from "../../../util/userDisplay";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { materialIdRange } from "./helpers";

// 開始/終了番号セル: 材料があれば材料QRへのリンク、無ければ "-"。
function materialQrCell(id: number): ReactNode {
  if (id < 0) {
    return "-";
  }
  return <DataLink type="material-qr" id={id} />;
}

export function IssuanceListPage({ pageContext }: { pageContext: PageContext }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const [rows, setRows] = useState<issuanceRow[] | null>(null);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    let active = true;
    setFetchError(false);
    api.issuance.get()
      .then((data) => { if (active) { setRows(data); } })
      .catch(() => { if (active) { setRows([]); setFetchError(true); } });
    return () => { active = false; };
  }, [api]);

  if (rows === null) {
    return <ContentLoading />;
  }
  if (fetchError) {
    return <FetchErrorAlert />;
  }

  const sorted = [...rows].sort((a, b) => b.createdAt - a.createdAt);

  return (
    <Stack p={3} gap={2} maxWidth={1400} mx="auto">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight="bold">発行一覧</Typography>
        <Button variant="contained" onClick={() => navigate("new/")}>新規発行</Button>
      </Stack>
      <Alert severity="info" sx={{ py: 0.5 }}>発行 = QRシールの印刷発行のみ。材料との紐付けは出荷時に行います。</Alert>

      <Paper variant="outlined">
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>発行日</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">発行総数</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">開始番号</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">終了番号</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>登録者</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>備考</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((iss) => {
                const range = materialIdRange(iss.Materials);
                return (
                  <TableRow key={iss.id} hover>
                    <TableCell><DataLink type="issuance" id={iss.id} /></TableCell>
                    <TableCell>{new Time(iss.createdAt).Format("YYYY/MM/dd HH:mm:ss")}</TableCell>
                    <TableCell align="right">{iss.Materials.length}</TableCell>
                    <TableCell align="right">{materialQrCell(range.head)}</TableCell>
                    <TableCell align="right">{materialQrCell(range.tail)}</TableCell>
                    <TableCell>{formatUserWithGroup(iss.User)}</TableCell>
                    <TableCell>{iss.remarks ?? ""}</TableCell>
                  </TableRow>
                );
              })}
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>発行履歴なし</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Stack>
  );
}
